import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchFilteredTree,
  fetchSubRolePerms,
  createSubRole,
  updateSubRole,
} from "redux/actions/RolesAndPermissionsAction";
import { toast } from "react-toastify";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const extractIds = (node) => {
  let ids = [...(node.permissions?.map((p) => p.id) || [])];
  if (node.subModules)
    node.subModules.forEach((sub) => {
      ids = [...ids, ...extractIds(sub)];
    });
  return ids;
};

// ─── Recursive Permission Accordion Node ────────────────────────────────────

const PermissionNode = ({
  node,
  checkedIds,
  onToggle,
  expanded,
  onExpandToggle,
}) => {
  const allIds = extractIds(node);
  const isChecked =
    allIds.length > 0 && allIds.every((id) => checkedIds.includes(id));
  const isIndeterminate =
    !isChecked && allIds.some((id) => checkedIds.includes(id));

  // Tracks which single sub-module (child of this node) is currently open,
  // so that expanding one sibling collapses the others — same exclusive
  // behavior as the top-level accordion group.
  const [expandedSubId, setExpandedSubId] = useState(null);

  const handleParentToggle = (e) => {
    e.stopPropagation();
    if (isChecked) {
      onToggle(checkedIds.filter((id) => !allIds.includes(id)));
    } else {
      onToggle([...new Set([...checkedIds, ...allIds])]);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => onExpandToggle(node.id)}
      disableGutters
      square
      sx={{
        mb: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        "&:before": { display: "none" },
        boxShadow: "none",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: "#f5f5f5",
          borderRadius: 1,
          minHeight: 0,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            my: 0.5,
          },
        }}
      >
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onClick={handleParentToggle}
          size="small"
        />
        <Typography variant="body2" fontWeight="700">
          {node.name}
          {node.businessType && (
            <Typography
              component="span"
              variant="body2"
              fontWeight="700"
              className="colorPrimary"
              sx={{ ml: 0.5 }}
            >
              ({node.businessType})
            </Typography>
          )}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 1 }}>
        {node.permissions?.map((p) => (
          <Box
            key={p.id}
            sx={{ display: "flex", alignItems: "center", py: 0.5, ml: 2 }}
          >
            <Checkbox
              checked={checkedIds.includes(p.id)}
              onChange={() =>
                onToggle(
                  checkedIds.includes(p.id)
                    ? checkedIds.filter((id) => id !== p.id)
                    : [...checkedIds, p.id],
                )
              }
              size="small"
            />
            <Typography variant="body2">{p.name}</Typography>
          </Box>
        ))}

        {node.subModules?.map((sub) => (
          <Box key={sub.id} sx={{ ml: 3 }}>
            <PermissionNode
              node={sub}
              checkedIds={checkedIds}
              onToggle={onToggle}
              expanded={expandedSubId === sub.id}
              onExpandToggle={(id) =>
                setExpandedSubId((prev) => (prev === id ? null : id))
              }
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

/**
 * SubRoleModal — used for both Create and Edit sub-role.
 *
 * Props:
 *  open         {boolean}
 *  onClose      {fn}
 *  parentRole   {object}  — always required
 *  subRole      {object|null} — null => create mode, object => edit mode
 *  onSuccess    {fn}  — callback after successful create/update
 */
export default function SubRoleModal({
  open,
  onClose,
  parentRole,
  subRole,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const { filteredTree, loading } = useSelector((state) => state.rbacReducer);

  const isEditing = !!subRole;

  const [subRoleName, setSubRoleName] = useState("");
  const [checkedIds, setCheckedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedModuleId, setExpandedModuleId] = useState(null);

  // Reset form every time modal opens
  useEffect(() => {
    if (open && parentRole?.id) {
      setSubRoleName(subRole?.name || "");
      setCheckedIds([]);
      setExpandedModuleId(null);
      dispatch(fetchFilteredTree(parentRole.id));

      if (isEditing && subRole?.id) {
        dispatch(fetchSubRolePerms(subRole.id)).then((res) => {
          if (res?.success)
            setCheckedIds(res.data?.assignedPermissionIds || []);
        });
      }
    }
  }, [open, parentRole?.id, subRole?.id]);

  const handleSubmit = () => {
    if (!subRoleName.trim()) return;
    setSaving(true);

    const payload = {
      parentRoleId: parentRole.id,
      name: subRoleName.trim(),
      permissionIds: checkedIds,
    };

    const action = isEditing
      ? dispatch(updateSubRole(subRole.id, payload))
      : dispatch(createSubRole(payload));

    action.then((res) => {
      setSaving(false);
      if (res?.success) {
        toast.success(isEditing ? "Sub-role updated!" : "Sub-role created!");
        onSuccess && onSuccess();
        onClose();
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {isEditing ? "Update Sub Role" : "Add New Sub Role"}
          </Typography>
          {parentRole && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              <Chip
                label={`Parent Role: ${parentRole.name}`}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip
                label={`Platform: ${parentRole.platform}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Business Type: ${parentRole.businessType}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Role Type: ${parentRole.roleType}`}
                size="small"
                variant="outlined"
              />
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Sub Role Name */}
        <TextField
          fullWidth
          label="Sub Role Name"
          value={subRoleName}
          onChange={(e) => setSubRoleName(e.target.value)}
          size="small"
          required
          placeholder="Enter sub role name"
          sx={{ mb: 3 }}
        />

        {/* Permissions Tree */}
        <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
          Select Permissions
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          mb={1.5}
          display="block"
        >
          Only permissions granted to the parent role are available.
        </Typography>

        <Box
          sx={{
            maxHeight: 360,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            p: 1.5,
            borderRadius: 1,
            bgcolor: "#fafafa",
          }}
        >
          {loading && !filteredTree.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredTree.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              No permissions available for this parent role.
            </Typography>
          ) : (
            filteredTree.map((mod) => (
              <PermissionNode
                key={mod.id}
                node={mod}
                checkedIds={checkedIds}
                onToggle={setCheckedIds}
                expanded={expandedModuleId === mod.id}
                onExpandToggle={(id) =>
                  setExpandedModuleId((prev) => (prev === id ? null : id))
                }
              />
            ))
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || loading || !subRoleName.trim()}
        >
          {saving ? "Saving..." : isEditing ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
